import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../App';
import { useTenant } from '../context/TenantContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  UtensilsCrossed, FolderOpen, HardDrive, Languages, 
  Zap, ArrowRight, X, CreditCard, Calendar, CheckCircle,
  QrCode, ExternalLink, Image, Eye
} from 'lucide-react';

const Dashboard = () => {
  const { t } = useTranslation();
  const { token, API, user } = useContext(AuthContext);
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(res.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="text-center p-6">{t('messages.loading')}</div>;

  const isStarterPlan = !tenant?.plan || tenant?.plan === 'STARTER' || tenant?.plan === 'starter';
  const isPaidPlan = tenant?.plan && (tenant.plan.toUpperCase() === 'PRO' || tenant.plan.toUpperCase() === 'ENTERPRISE');
  
  // Get subscription info from user context
  const subscriptionStatus = user?.subscription_status || 'active';
  const billingPeriod = user?.billing_period || 'monthly';
  const paymentDueDate = user?.payment_due_date;
  const daysUntilDue = user?.days_until_due;

  // Calculate usage percentages
  const menuItemsUsage = stats?.menu_items?.limit 
    ? Math.round((stats.menu_items.count / stats.menu_items.limit) * 100)
    : 0;
  const categoriesUsage = stats?.categories?.limit 
    ? Math.round((stats.categories.count / stats.categories.limit) * 100)
    : 0;
  const storageUsage = stats?.storage?.limit_mb 
    ? Math.round((stats.storage.used_mb / stats.storage.limit_mb) * 100)
    : 0;

  const getUsageColor = (percentage) => {
    if (percentage >= 90) return { color: '#ef4444', bg: '#fee2e2' };
    if (percentage >= 70) return { color: '#f59e0b', bg: '#fef3c7' };
    return { color: '#10b981', bg: '#d1fae5' };
  };

  const publicMenuUrl = tenant?.slug 
    ? `${window.location.origin}/menu/${tenant.slug}` 
    : null;

  return (
    <div className="dashboard">
      {/* Upgrade Banner for STARTER tenants */}
      {isStarterPlan && (
        <div className="mb-6 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-4">
              <div className="bg-white/20 rounded-lg p-3">
                <Zap className="w-8 h-8" />
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2">{t('dashboard.unlockProFeatures')}</h3>
                <p className="text-blue-50 mb-4">
                  {t('dashboard.upgradeDescription')}
                </p>
                <button
                  onClick={() => navigate('/admin/upgrade')}
                  className="inline-flex items-center px-6 py-3 bg-white text-blue-600 rounded-lg font-semibold hover:bg-blue-50 transition-colors shadow-md"
                >
                  {t('dashboard.viewPlans')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </button>
              </div>
            </div>
            <button
              onClick={(e) => e.currentTarget.parentElement.parentElement.style.display = 'none'}
              className="text-white/80 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Subscription Status Card for paid plans */}
      {isPaidPlan && (
        <div className="mb-6 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-lg ${
                subscriptionStatus === 'active' ? 'bg-green-100' :
                subscriptionStatus === 'past_due' ? 'bg-amber-100' : 'bg-red-100'
              }`}>
                {subscriptionStatus === 'active' ? (
                  <CheckCircle className={`w-6 h-6 text-green-600`} />
                ) : (
                  <CreditCard className={`w-6 h-6 ${
                    subscriptionStatus === 'past_due' ? 'text-amber-600' : 'text-red-600'
                  }`} />
                )}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-gray-900">
                    {tenant?.plan?.toUpperCase()} {t('dashboard.plan')}
                  </h3>
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                    subscriptionStatus === 'active' ? 'bg-green-100 text-green-700' :
                    subscriptionStatus === 'past_due' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {subscriptionStatus === 'active' ? t('dashboard.statusActive') :
                     subscriptionStatus === 'past_due' ? t('dashboard.statusPaymentDue') : t('dashboard.statusSuspended')}
                  </span>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  {t('dashboard.billed')} {billingPeriod === 'annual' ? t('dashboard.annually') : t('dashboard.monthly')}
                  {paymentDueDate && subscriptionStatus === 'active' && (
                    <> • {t('dashboard.nextPayment')}: {new Date(paymentDueDate).toLocaleDateString()}</>
                  )}
                  {daysUntilDue !== null && daysUntilDue !== undefined && subscriptionStatus === 'active' && daysUntilDue > 0 && (
                    <> ({daysUntilDue} {t('dashboard.days')})</>
                  )}
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate('/admin/upgrade')}
              className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
            >
              <Calendar className="w-4 h-4 inline-block mr-2" />
              {t('dashboard.manageSubscription')}
            </button>
          </div>
        </div>
      )}

      {/* Public Menu Link Card */}
      {publicMenuUrl && (
        <div className="mb-6 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-purple-100">
                <QrCode className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">{t('dashboard.yourPublicMenu')}</h3>
                <p className="text-sm text-gray-500 mt-1">
                  {publicMenuUrl}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => navigator.clipboard.writeText(publicMenuUrl)}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors border border-gray-200"
              >
                {t('dashboard.copyLink')}
              </button>
              <a
                href={publicMenuUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors flex items-center gap-2"
              >
                <ExternalLink className="w-4 h-4" />
                {t('dashboard.viewMenu')}
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Usage Stats Grid */}
      <div className="stats-grid">
        {/* Menu Items Card */}
        <div className="stat-card" data-testid="menu-items">
          <div className="stat-icon" style={{ background: getUsageColor(menuItemsUsage).bg }}>
            <UtensilsCrossed size={24} color={getUsageColor(menuItemsUsage).color} />
          </div>
          <div className="stat-content">
            <div className="stat-title">{t('dashboard.menuItems')}</div>
            <div className="stat-value" data-testid="menu-items-value">
              {stats?.menu_items?.count || 0}
              {stats?.menu_items?.limit && (
                <span className="stat-limit">/ {stats.menu_items.limit}</span>
              )}
            </div>
            {stats?.menu_items?.limit && (
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ 
                    width: `${Math.min(menuItemsUsage, 100)}%`,
                    backgroundColor: getUsageColor(menuItemsUsage).color
                  }}
                />
              </div>
            )}
            <div className="stat-meta">
              <Eye size={14} /> {stats?.menu_items?.available || 0} {t('dashboard.available')}
              {stats?.storage?.limit_mb > 0 && (
                <>
                  <span className="mx-2">•</span>
                  <Image size={14} /> {stats?.menu_items?.with_images || 0} {t('dashboard.withImages')}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Categories Card */}
        <div className="stat-card" data-testid="categories">
          <div className="stat-icon" style={{ background: getUsageColor(categoriesUsage).bg }}>
            <FolderOpen size={24} color={getUsageColor(categoriesUsage).color} />
          </div>
          <div className="stat-content">
            <div className="stat-title">{t('dashboard.categories')}</div>
            <div className="stat-value" data-testid="categories-value">
              {stats?.categories?.count || 0}
              {stats?.categories?.limit && (
                <span className="stat-limit">/ {stats.categories.limit}</span>
              )}
            </div>
            {stats?.categories?.limit && (
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ 
                    width: `${Math.min(categoriesUsage, 100)}%`,
                    backgroundColor: getUsageColor(categoriesUsage).color
                  }}
                />
              </div>
            )}
            <div className="stat-meta">
              {stats?.categories?.root || 0} {t('dashboard.rootCategories')}
              {stats?.categories?.subcategories > 0 && (
                <>
                  <span className="mx-2">•</span>
                  {stats.categories.subcategories} {t('dashboard.subcategories')}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Languages Card */}
        <div className="stat-card" data-testid="languages">
          <div className="stat-icon" style={{ background: '#dbeafe' }}>
            <Languages size={24} color="#3b82f6" />
          </div>
          <div className="stat-content">
            <div className="stat-title">{t('dashboard.languages')}</div>
            <div className="stat-value" data-testid="languages-value">
              {stats?.languages?.count || 1}
              {stats?.languages?.limit && (
                <span className="stat-limit">/ {stats.languages.limit}</span>
              )}
            </div>
            <div className="stat-meta">
              {(stats?.languages?.supported || ['tr']).map(lang => lang.toUpperCase()).join(', ')}
            </div>
          </div>
        </div>

        {/* Storage Card - Only show for PRO/Enterprise plans that can upload images */}
        {stats?.storage?.limit_mb > 0 && (
          <div className="stat-card" data-testid="storage">
            <div className="stat-icon" style={{ background: getUsageColor(storageUsage).bg }}>
              <HardDrive size={24} color={getUsageColor(storageUsage).color} />
            </div>
            <div className="stat-content">
              <div className="stat-title">{t('dashboard.storage')}</div>
              <div className="stat-value" data-testid="storage-value">
                {stats?.storage?.used_mb?.toFixed(1) || 0} MB
                <span className="stat-limit">/ {stats.storage.limit_mb} MB</span>
              </div>
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ 
                    width: `${Math.min(storageUsage, 100)}%`,
                    backgroundColor: getUsageColor(storageUsage).color
                  }}
                />
              </div>
              <div className="stat-meta">
                {t('dashboard.forImages')}
              </div>
            </div>
          </div>
        )}

        {/* Modifiers Card */}
        <div className="stat-card" data-testid="modifiers">
          <div className="stat-icon" style={{ background: '#fce7f3' }}>
            <UtensilsCrossed size={24} color="#ec4899" />
          </div>
          <div className="stat-content">
            <div className="stat-title">{t('dashboard.modifiers')}</div>
            <div className="stat-value" data-testid="modifiers-value">
              {stats?.modifiers?.count || 0}
            </div>
            <div className="stat-meta">
              {t('dashboard.itemAddons')}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="font-semibold text-gray-900 mb-4">{t('dashboard.quickActions')}</h3>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => navigate('/admin/menu')}
            className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {t('dashboard.manageMenu')}
          </button>
          <button
            onClick={() => navigate('/settings')}
            className="px-4 py-2 text-sm font-medium bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            {t('dashboard.settings')}
          </button>
          {publicMenuUrl && (
            <a
              href={publicMenuUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 text-sm font-medium bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors flex items-center gap-2"
            >
              <QrCode size={16} />
              {t('dashboard.viewPublicMenu')}
            </a>
          )}
        </div>
      </div>

      <style>{`
        .dashboard {
          width: 100%;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 20px;
        }

        .stat-card {
          background: hsl(var(--card));
          border-radius: 12px;
          padding: 24px;
          display: flex;
          gap: 16px;
          align-items: flex-start;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
          border: 1px solid hsl(var(--border));
          transition: transform 0.2s, box-shadow 0.2s, background-color 0.3s;
        }

        .stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .stat-icon {
          width: 56px;
          height: 56px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .stat-content {
          flex: 1;
          min-width: 0;
        }

        .stat-title {
          font-size: 13px;
          color: hsl(var(--muted-foreground));
          margin-bottom: 6px;
          font-weight: 500;
        }

        .stat-value {
          font-size: 28px;
          font-weight: 700;
          color: hsl(var(--foreground));
          font-family: 'Manrope', sans-serif;
        }

        .stat-limit {
          font-size: 16px;
          font-weight: 500;
          color: hsl(var(--muted-foreground));
          margin-left: 4px;
        }

        .stat-meta {
          font-size: 12px;
          color: hsl(var(--muted-foreground));
          margin-top: 8px;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .progress-bar {
          height: 6px;
          background: hsl(var(--border));
          border-radius: 3px;
          margin-top: 8px;
          overflow: hidden;
        }

        .progress-fill {
          height: 100%;
          border-radius: 3px;
          transition: width 0.3s ease;
        }

        @media (max-width: 768px) {
          .stats-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default Dashboard;
